import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Particles from "@/components/Particles";
import { Reveal } from "@/components/Reveal";

export default function FinalCTA() {
  return (
    <section
      id="start"
      data-testid="final-cta-section"
      className="relative overflow-hidden py-36 lg:py-48"
    >
      <Particles count={60} />
      <div className="absolute left-1/2 top-1/2 h-[600px] w-[900px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(216,192,138,0.1),transparent_60%)] blur-3xl" />
      <motion.img
        src="/assets/emblem.png"
        alt=""
        aria-hidden
        initial={{ opacity: 0, scale: 0.85, rotate: -8 }}
        whileInView={{ opacity: 0.16, scale: 1, rotate: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
        className="pointer-events-none absolute left-1/2 top-1/2 w-[560px] max-w-[90vw] -translate-x-1/2 -translate-y-1/2 object-contain"
      />

      <div className="relative mx-auto max-w-5xl px-6 text-center lg:px-10">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.4em] text-gold">
            Begin
          </p>
        </Reveal>
        <Reveal delay={0.12}>
          <h2
            className="mt-6 font-display text-5xl font-light leading-[1.05] tracking-tighter text-[#F5F5F5] md:text-7xl"
            data-testid="final-cta-headline"
          >
            One Mind.{" "}
            <span className="gold-text font-normal">Every Department.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.24}>
          <p className="mx-auto mt-8 max-w-xl text-base leading-relaxed text-[#A3A3A3] md:text-lg">
            Powered by Alkyrvis. Available 24 Hours A Day. 365 Days A Year.
          </p>
        </Reveal>
        <Reveal delay={0.36}>
          <div className="mt-12 flex justify-center">
            <motion.button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent("open-demo-modal"))}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              data-testid="final-cta-button"
              className="group inline-flex items-center gap-3 rounded-full bg-gold px-10 py-5 font-mono text-sm font-medium uppercase tracking-[0.2em] text-[#050505] shadow-[0_0_60px_rgba(216,192,138,0.35)] transition-[box-shadow,background-color] duration-300 hover:bg-gold-bright hover:shadow-[0_0_90px_rgba(216,192,138,0.55)]"
            >
              Start Building The Future
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
            </motion.button>
          </div>
        </Reveal>
      </div>

      <footer
        className="relative mt-36 border-t border-white/5"
        data-testid="site-footer"
      >
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-8 px-6 py-12 md:flex-row lg:px-10">
          <div className="flex items-center gap-3">
            <img
              src="/assets/emblem.png"
              alt="Alkyrvis emblem"
              className="h-8 w-8 object-contain drop-shadow-[0_0_10px_rgba(216,192,138,0.4)]"
            />
            <div>
              <p className="font-display text-sm font-medium uppercase tracking-[0.4em] text-[#F5F5F5]">
                Alkyrvis
              </p>
              <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3]">
                The AI Operating System for Business
              </p>
            </div>
          </div>
          <div className="flex items-center gap-8">
            {["Workforce", "Capabilities", "Demo", "Why", "Pricing"].map((l) => (
              <a
                key={l}
                href={`#${l === "Why" ? "why" : l === "Demo" ? "demo" : l.toLowerCase()}`}
                className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A3A3A3] transition-colors duration-300 hover:text-gold"
                data-testid={`footer-link-${l.toLowerCase()}`}
              >
                {l}
              </a>
            ))}
          </div>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/30">
            © 2026 Alkyrvis. The future is employed.
          </p>
        </div>
      </footer>
    </section>
  );
}
